import React from 'react';
import { Modal } from '../../CompositeComponents/Modal/Modal';
import { Card } from '../../CompositeComponents/Card/Card';
import { Button } from '../../BasicComponents/Button/Button';
import { formatMoney } from '../../../utils/currency';
import type { AlternativeItem, AlternativeItemPopupProps } from './types';

function SkeletonRow() {
  return (
    <div className="flex items-center gap-3 p-3 rounded-lg border border-neutral-100 dark:border-neutral-700 animate-pulse">
      <div className="h-10 w-10 rounded bg-neutral-200 dark:bg-neutral-700 shrink-0" />
      <div className="flex-1 space-y-1.5">
        <div className="h-3 rounded bg-neutral-200 dark:bg-neutral-700 w-3/4" />
        <div className="h-2.5 rounded bg-neutral-200 dark:bg-neutral-700 w-1/2" />
      </div>
      <div className="h-8 w-20 rounded bg-neutral-200 dark:bg-neutral-700 shrink-0" />
    </div>
  );
}

// ─── AlternativeItemPopup ─────────────────────────────────────────────────────

export function AlternativeItemPopup({
  isOpen,
  onClose,
  originalItemName,
  alternatives,
  onSelect,
  currency = 'INR',
  className,
}: AlternativeItemPopupProps & { className?: string }) {
  const fmt = (paise: number): string => formatMoney(paise, currency);
  const isLoading = false; // loading prop not in existing types; use false

  return (
    <Modal
      open={isOpen}
      onClose={onClose}
      title={`Alternatives for "${originalItemName}"`}
      aria-label={`Alternative items for ${originalItemName}`}
    >
      <div className={`space-y-3 ${className ?? ''}`}>
        {/* Out of stock notice */}
        <div className="flex items-center gap-2 text-sm text-warning-700 dark:text-warning-400 bg-warning-50 dark:bg-warning-900/20 rounded-lg px-3 py-2">
          <svg className="h-4 w-4 shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <path d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
            <line x1="12" y1="9" x2="12" y2="13" /><line x1="12" y1="17" x2="12.01" y2="17" />
          </svg>
          <span><strong>{originalItemName}</strong> is out of stock. Select an alternative below.</span>
        </div>

        {isLoading ? (
          <div className="space-y-2">
            <SkeletonRow /><SkeletonRow /><SkeletonRow />
          </div>
        ) : alternatives.length === 0 ? (
          <div className="text-center py-8 text-neutral-500 dark:text-neutral-400 text-sm">
            No alternatives in this category
          </div>
        ) : (
          <div className="space-y-2 max-h-72 overflow-y-auto">
            {alternatives.map((item: AlternativeItem) => (
              <Card key={item.id} variant="outlined" padding="sm">
                <div className="flex items-center gap-3">
                  <div className="h-10 w-10 rounded-md bg-neutral-100 dark:bg-neutral-700 flex items-center justify-center shrink-0 overflow-hidden">
                    {item.imageUrl ? (
                      <img src={item.imageUrl} alt={item.name} className="h-full w-full object-cover" />
                    ) : (
                      <svg className="h-5 w-5 text-neutral-400" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                        <rect x="3" y="3" width="18" height="18" rx="2" />
                        <path d="M3 9h18M9 21V9" />
                      </svg>
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-neutral-800 dark:text-neutral-100 truncate">{item.name}</p>
                    <p className="text-xs text-neutral-500 dark:text-neutral-400">
                      {item.sku} · {item.stock > 0 ? `${item.stock} in stock` : 'Out of stock'}
                    </p>
                  </div>
                  <div className="text-right shrink-0">
                    <p className="text-sm font-semibold text-neutral-800 dark:text-neutral-100">{fmt(item.price)}</p>
                    <Button
                      variant="primary"
                      size="sm"
                      disabled={item.stock === 0}
                      onClick={() => { onSelect(item); onClose(); }}
                      aria-label={`Replace with ${item.name}`}
                    >
                      Replace
                    </Button>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        )}

        <div className="flex justify-end pt-1">
          <Button variant="ghost" size="sm" onClick={onClose} aria-label="Dismiss and remove from cart">
            Remove item
          </Button>
        </div>
      </div>
    </Modal>
  );
}
